import React, { useState } from 'react';
import { Button, Flex, Input, Space, Tag, Tooltip, Typography } from 'antd';
import { CloseOutlined, LinkOutlined, PlusOutlined, ShopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import HintLabel from '../../shared/ui/HintLabel';
import {
  MAX_NOTE_LENGTH,
  MAX_SUPPLIERS,
  MAX_URL_LENGTH,
  addSupplierLink,
  displayHost,
  supplierSource,
  type SupplierLink,
} from '../../shared/lib/supplierLinks';

interface SupplierLinksProps {
  links: SupplierLink[];
  onChange: (next: SupplierLink[]) => void;
}

const SOURCE_COLOR: Record<string, string> = {
  '1688': 'orange',
  alibaba: 'gold',
  aliexpress: 'red',
  made_in_china: 'blue',
};

/**
 * Ссылки на поставщика (WS 15587410, п.7): Alibaba, 1688, биржа — любой адрес.
 * Сохраняются в расчёте и открываются в новой вкладке.
 */
const SupplierLinks: React.FC<SupplierLinksProps> = ({ links, onChange }) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const res = addSupplierLink(links, url, note);
    if (!res.ok) {
      setError(t(`idea_calc.supplier.error_${res.reason}`, { max: MAX_SUPPLIERS }));
      return;
    }
    onChange(res.links);
    setUrl('');
    setNote('');
    setError(null);
    setAdding(false);
  };

  return (
    <div data-testid="supplier-links">
      <Flex align="center" gap="small" wrap>
        <Typography.Text type="secondary">
          <HintLabel hint={t('idea_calc.supplier.tips.label')}>{t('idea_calc.supplier.label')}</HintLabel>
        </Typography.Text>
        {links.map((link) => {
          const source = supplierSource(link.url);
          return (
            <Tag
              key={link.url}
              color={SOURCE_COLOR[source]}
              closable
              closeIcon={<CloseOutlined aria-label={t('idea_calc.supplier.remove')} />}
              onClose={(e) => {
                e.preventDefault();
                onChange(links.filter((l) => l.url !== link.url));
              }}
              style={{ marginInlineEnd: 0 }}
            >
              <Tooltip title={link.url}>
                <a href={link.url} target="_blank" rel="noopener noreferrer nofollow">
                  <LinkOutlined /> {source === 'other' ? displayHost(link.url) : t(`idea_calc.supplier.source.${source}`)}
                  {link.note ? ` · ${link.note}` : ''}
                </a>
              </Tooltip>
            </Tag>
          );
        })}
        {!adding && links.length < MAX_SUPPLIERS && (
          <Button size="small" icon={links.length ? <PlusOutlined /> : <ShopOutlined />} onClick={() => setAdding(true)}>
            {links.length ? t('idea_calc.supplier.add_more') : t('idea_calc.supplier.add_button')}
          </Button>
        )}
      </Flex>

      {adding && (
        <Flex vertical gap={4} style={{ marginTop: 6 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              size="small"
              autoFocus
              placeholder={t('idea_calc.supplier.url_placeholder')}
              value={url}
              maxLength={MAX_URL_LENGTH}
              status={error ? 'error' : undefined}
              onChange={(e) => {
                setUrl(e.target.value);
                setError(null);
              }}
              onPressEnter={add}
              style={{ flex: 3 }}
            />
            <Input
              size="small"
              placeholder={t('idea_calc.supplier.note_placeholder')}
              value={note}
              maxLength={MAX_NOTE_LENGTH}
              onChange={(e) => setNote(e.target.value)}
              onPressEnter={add}
              style={{ flex: 2 }}
            />
            <Button size="small" type="primary" disabled={!url.trim()} onClick={add}>
              {t('idea_calc.supplier.save')}
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAdding(false);
                setUrl('');
                setNote('');
                setError(null);
              }}
            >
              {t('idea_calc.supplier.cancel')}
            </Button>
          </Space.Compact>
          {error && <Typography.Text type="danger" style={{ fontSize: 12 }}>{error}</Typography.Text>}
        </Flex>
      )}
    </div>
  );
};

export default SupplierLinks;
